import { store } from 'careand-redux/store';
import { ActivePhysician } from 'careand-redux/selectors';
import { PhysicianTranscriptionProviderEnum } from 'generated/api';
import { AudioService, TeeAudioService } from './MicService';
import { DeepgramTranscribeService, DeepgramTranscribeOptions } from './DeepgramTranscribeService';

/**
 * Per-physician transcription routing (Physician.transcription_provider):
 * - 'aws-transcribe' (or unset): AWS only, exactly the pre-Deepgram behavior
 * - 'deepgram': Deepgram drives the transcription UI instead of AWS
 * - 'both': AWS drives the UI; Deepgram runs in shadow on a tee of the same
 *   audio and its transcript is checkpointed to the appointment's
 *   AppointmentTranscriptionComparison twin for quality review. Only the
 *   appointment flows shadow — solo dictation (mono, no diarization) is the
 *   easy case for both providers and is deliberately excluded from the pilot.
 */
export function getTranscriptionProviderSetting(): PhysicianTranscriptionProviderEnum {
  const physician = ActivePhysician(store.getState());
  return physician?.transcription_provider ?? PhysicianTranscriptionProviderEnum.AwsTranscribe;
}

/**
 * When the active physician is in shadow mode ('both'), tees the audio source
 * and returns a Deepgram shadow service reading the second branch; the primary
 * provider must then consume `primaryAudio`. Any setup failure falls back to
 * AWS-only — the shadow must never break the real transcription.
 */
export function maybeCreateDeepgramShadow(
  audioSource: AudioService,
  deepgramOptions: DeepgramTranscribeOptions,
): { primaryAudio: AudioService; shadow?: DeepgramTranscribeService } {
  if (getTranscriptionProviderSetting() !== PhysicianTranscriptionProviderEnum.Both) {
    return { primaryAudio: audioSource };
  }

  try {
    const tee = new TeeAudioService(audioSource);
    // The primary owns the source: when it stops (or dies), the microphone is
    // released and the shadow's follower branch drains — the shadow can never
    // keep the mic hot or restart it on its own.
    const service = new DeepgramTranscribeService(
      tee.branch(),
      undefined,
      (err) => console.error('[Deepgram shadow] error:', err),
      deepgramOptions,
    );
    return { primaryAudio: tee.branch({ owner: true }), shadow: service };
  } catch (err: any) {
    console.error('[Deepgram shadow] setup failed, continuing AWS-only:', err);
    return { primaryAudio: audioSource };
  }
}
